import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const AuthContext = createContext(null);

const TOKEN_KEY = "tmm_token_v2";
const LEGACY_USERS_KEY = "tmm_users_v1";
const LEGACY_CURRENT_KEY = "tmm_current_user_v1";
const MIGRATED_KEY = "tmm_legacy_migrated_v1";
const API = process.env.REACT_APP_BACKEND_URL;

const loadLegacyUsers = () => {
  try {
    return JSON.parse(localStorage.getItem(LEGACY_USERS_KEY) || "[]");
  } catch {
    return [];
  }
};

const clearLegacyUsers = () => {
  try {
    localStorage.removeItem(LEGACY_USERS_KEY);
    localStorage.removeItem(LEGACY_CURRENT_KEY);
  } catch {
    /* ignore */
  }
};

async function api(path, { method = "GET", body, token } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = Array.isArray(data?.detail)
      ? data.detail[0]?.msg || "Something went wrong."
      : data?.detail || data?.message || "Something went wrong.";
    return { ok: false, error: message };
  }
  return { ok: true, data };
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  });
  const [current, setCurrent] = useState(null);
  const [users, setUsers] = useState([]);
  const [ready, setReady] = useState(false);
  // Skip the /me refresh right after signup/login — we already have the fresh
  // user object, and a transient network hiccup on /me was silently reverting
  // the UI back to the sign-up form.
  const skipNextMeRef = React.useRef(false);

  // Persist token
  useEffect(() => {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }, [token]);

  // Refresh the community grid
  const refreshUsers = useCallback(async () => {
    const r = await api("/api/auth/list");
    if (r.ok) setUsers(r.data.users || []);
  }, []);

  // Restore session on load
  useEffect(() => {
    let stop = false;
    (async () => {
      // If we're mid-OAuth callback, defer to that handler
      if (window.location.hash?.includes("session_id=")) {
        setReady(true);
        return;
      }
      if (skipNextMeRef.current) {
        skipNextMeRef.current = false;
        setReady(true);
        return;
      }
      if (token) {
        const r = await api("/api/auth/me", { token });
        if (!stop) {
          if (r.ok) {
            setCurrent(r.data.user);
          } else {
            // Only clear the token on an explicit auth error — a network
            // hiccup shouldn't kick a freshly signed-up user back out.
            if (r.error && /invalid|expired|not authenticated/i.test(r.error)) {
              setToken(null);
              setCurrent(null);
            }
          }
        }
      }
      await refreshUsers();
      if (!stop) setReady(true);
    })();
    return () => {
      stop = true;
    };
  }, [token, refreshUsers]);

  const register = useCallback(
    async (payload) => {
      const r = await api("/api/auth/register", { method: "POST", body: payload });
      if (!r.ok) return { ok: false, error: r.error };
      skipNextMeRef.current = true;
      setCurrent(r.data.user);
      setToken(r.data.token);
      refreshUsers();
      return { ok: true, user: r.data.user };
    },
    [refreshUsers]
  );

  const createInvite = useCallback(
    async (invitee_email = "") => {
      return api("/api/invites", {
        method: "POST",
        body: { invitee_email },
        token,
      });
    },
    [token]
  );

  const listMyInvites = useCallback(async () => {
    return api("/api/invites/mine", { token });
  }, [token]);

  const revokeInvite = useCallback(
    async (code) => {
      return api(`/api/invites/${encodeURIComponent(code)}`, {
        method: "DELETE",
        token,
      });
    },
    [token]
  );

  const lookupInvite = useCallback(async (code) => {
    return api(`/api/invites/lookup/${encodeURIComponent(code)}`);
  }, []);

  const requestMemberCountAccess = useCallback(async () => {
    const r = await api("/api/auth/me/count-access-request", { method: "POST", token });
    if (r.ok) {
      setCurrent(r.data.user);
    }
    return r;
  }, [token]);

  const login = useCallback(
    async (email, password) => {
      const r = await api("/api/auth/login", {
        method: "POST",
        body: { email, password },
      });
      if (r.ok) {
        skipNextMeRef.current = true;
        setCurrent(r.data.user);
        setToken(r.data.token);
        refreshUsers();
        return { ok: true, user: r.data.user };
      }

      // Auto-migrate legacy localStorage account on first login attempt
      if (!localStorage.getItem(MIGRATED_KEY)) {
        const legacy = loadLegacyUsers();
        const match = legacy.find(
          (u) =>
            (u.email || "").toLowerCase() === email.trim().toLowerCase() &&
            u.password === password
        );
        if (match) {
          const reg = await api("/api/auth/register", {
            method: "POST",
            body: {
              email: match.email,
              password,
              name: match.name || match.email,
              expertise: match.expertise || "",
              bio: match.bio || "",
              website: match.website || "",
              instagram: match.instagram || "",
              twitter: match.twitter || "",
              phone: match.phone || "",
              avatar: match.avatar || null,
              security_question: "Migrated account — set a new security question in your profile",
              security_answer: password,
            },
          });
          if (reg.ok) {
            try {
              localStorage.setItem(MIGRATED_KEY, "1");
            } catch {
              /* ignore */
            }
            clearLegacyUsers();
            skipNextMeRef.current = true;
            setCurrent(reg.data.user);
            setToken(reg.data.token);
            refreshUsers();
            return { ok: true, user: reg.data.user, migrated: true };
          }
        }
      }
      return { ok: false, error: r.error };
    },
    [refreshUsers]
  );

  const logout = useCallback(async () => {
    try {
      await api("/api/auth/logout", { method: "POST", token });
    } catch {
      /* ignore */
    }
    setToken(null);
    setCurrent(null);
  }, [token]);

  const updateProfile = useCallback(
    async (patch) => {
      const r = await api("/api/auth/me", { method: "PATCH", body: patch, token });
      if (r.ok) {
        setCurrent(r.data.user);
        await refreshUsers();
        return { ok: true, user: r.data.user };
      }
      return { ok: false, error: r.error };
    },
    [token, refreshUsers]
  );

  const getSecurityQuestion = useCallback(async (email) => {
    return api(`/api/auth/security-question?email=${encodeURIComponent(email)}`);
  }, []);

  const resetPassword = useCallback(
    async ({ email, security_answer, new_password }) => {
      const r = await api("/api/auth/reset-password", {
        method: "POST",
        body: { email, security_answer, new_password },
      });
      if (r.ok) {
        setToken(r.data.token);
        setCurrent(r.data.user);
        await refreshUsers();
        return { ok: true, user: r.data.user };
      }
      return { ok: false, error: r.error };
    },
    [refreshUsers]
  );

  const applyGoogleSession = useCallback(
    async (data) => {
      setToken(data.token);
      setCurrent(data.user);
      await refreshUsers();
    },
    [refreshUsers]
  );

  const resetWithToken = useCallback(
    async ({ token: resetToken, new_password }) => {
      const r = await api("/api/auth/reset-with-token", {
        method: "POST",
        body: { token: resetToken, new_password },
      });
      if (r.ok) {
        skipNextMeRef.current = true;
        setToken(r.data.token);
        setCurrent(r.data.user);
        await refreshUsers();
        return { ok: true, user: r.data.user };
      }
      return { ok: false, error: r.error };
    },
    [refreshUsers]
  );

  const value = useMemo(
    () => ({
      token,
      users,
      current,
      ready,
      register,
      login,
      logout,
      updateProfile,
      getSecurityQuestion,
      resetPassword,
      resetWithToken,
      applyGoogleSession,
      refreshUsers,
      createInvite,
      listMyInvites,
      revokeInvite,
      lookupInvite,
      requestMemberCountAccess,
    }),
    [
      token,
      users,
      current,
      ready,
      register,
      login,
      logout,
      updateProfile,
      getSecurityQuestion,
      resetPassword,
      resetWithToken,
      applyGoogleSession,
      refreshUsers,
      createInvite,
      listMyInvites,
      revokeInvite,
      lookupInvite,
      requestMemberCountAccess,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
